import { Dispatch, FC, SetStateAction, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Box, FormHelperText, Stack, Typography } from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { differenceInMinutes } from "date-fns";
import __ from "src/utils/Translations";
import formatHourLabel from "src/utils/Company/Absences/formatHourLabel";
import CustomPickersDay from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker/CustomPickersDay";
import { IAbsenceRequestDay } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import {
  defaultBoxStyles,
  hourFieldStyles,
  hourDividerStyles,
} from "./AbsenceHourPicker.styles";

interface IAbsenceHourPickerProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  plannedDays: string[];
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
  submitLimitationFrom: string; // ISO date in format YYYY-MM-DD
  submitLimitationTo: string; // ISO date in format YYYY-MM-DD
  hoursCount: number;
  setHoursCount: Dispatch<SetStateAction<number>>;
  handleMonthChange: (month: Date) => void;
}

const AbsenceHourPicker: FC<IAbsenceHourPickerProps> = ({
  absenceRequestDays,
  excludedDays,
  plannedDays,
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  submitLimitationFrom,
  submitLimitationTo,
  hoursCount,
  setHoursCount,
  handleMonthChange,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();
  const startTime: Date | null = useWatch({ name: "startTime" });
  const endTime: Date | null = useWatch({ name: "endTime" });

  useEffect(() => {
    if (startTime && endTime && endTime > startTime) {
      setHoursCount(differenceInMinutes(endTime, startTime) / 60);
    } else {
      setHoursCount(0);
    }
  }, [startTime, endTime, trigger, setHoursCount]);

  return (
    <Box sx={defaultBoxStyles}>
      <Controller
        control={control}
        name="date"
        render={({
          field: { value, ref, onChange },
          fieldState: { error },
        }) => (
          <DatePicker
            label={__("Wybierz datę")}
            value={value}
            onChange={onChange}
            disableHighlightToday
            onMonthChange={handleMonthChange}
            dayOfWeekFormatter={(date: Date) =>
              date.toLocaleString("pl", { weekday: "short" })
            }
            slots={{
              day: (props) =>
                CustomPickersDay({
                  absenceRequestDays,
                  excludedDays,
                  plannedDays,
                  multipleRequestsPerDayAllowed,
                  billInCalendarDays,
                  submitLimitationFrom,
                  submitLimitationTo,
                  ...props,
                }),
            }}
            slotProps={{
              textField: {
                required: true,
                error: !!error,
                helperText: error?.message,
                ref,
                sx: { minWidth: "295px", background: "white" },
              },
            }}
            data-testid="absence-hour-picker"
          />
        )}
      />
      <Stack
        direction="row"
        sx={{ flexWrap: "nowrap", ...defaultBoxStyles }}
        spacing={2}
        useFlexGap
      >
        <Controller
          name="startTime"
          control={control}
          render={({
            field: { value, ref, onChange },
            fieldState: { error },
          }) => (
            <TimePicker
              label={__("Początek nieobecności")}
              value={value}
              onChange={onChange}
              ampm={false}
              views={["hours", "minutes"]}
              timeSteps={{ minutes: 30 }}
              sx={hourFieldStyles}
              slotProps={{
                textField: {
                  error: !!error,
                  ref,
                  id: "startTime",
                },
                actionBar: {
                  actions: [],
                },
              }}
              data-testid="absence-hour-picker__start"
            />
          )}
        />
        <Typography component="span" sx={hourDividerStyles}>
          –
        </Typography>
        <Controller
          name="endTime"
          control={control}
          render={({
            field: { value, ref, onChange },
            fieldState: { error },
          }) => (
            <TimePicker
              label={__("Koniec nieobecności")}
              value={value}
              onChange={onChange}
              ampm={false}
              views={["hours", "minutes"]}
              timeSteps={{ minutes: 30 }}
              sx={hourFieldStyles}
              slotProps={{
                textField: {
                  error: !!error,
                  ref,
                  id: "endTime",
                },
                actionBar: {
                  actions: [],
                },
              }}
              data-testid="absence-hour-picker__end"
            />
          )}
        />
        <Typography
          variant="body2"
          component="span"
          sx={{
            alignSelf: "center",
          }}
        >
          {__("SUMA")}: <strong>{formatHourLabel(hoursCount)}</strong>
        </Typography>
      </Stack>
      {errors?.startTime && (
        <FormHelperText error={!!errors?.startTime}>
          {`${errors?.startTime?.message}`}
        </FormHelperText>
      )}
    </Box>
  );
};

export default AbsenceHourPicker;
